import { useRouter } from 'next/router'
import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { PhotoView } from 'react-photo-view'

import cn from 'classnames'

import { DEFAULT_NEW_PRODUCT_DURATION_IN_DAYS } from 'components/_pages/search/refinement-by-query/constants'
import { getNewProductDurationRange } from 'components/_pages/search/search-page-configure/utils'
import { RegularSearch } from 'components/icon/regular'
import ImageComponent from 'components/image'
import FavouriteButton from 'components/molecules/favourite-button'
import { errorToast, successToast } from 'components/notification-toast'
import ProductCard, { Product } from 'components/product-card'
import Search from 'components/search'
import Select from 'components/select'
import { NoResults } from 'components/table/components/no-results'
import ProductListContainer from 'components/templates/product-list-container'
import { useGetFavouriteProducts } from 'features/retailer/favourites/hooks/use-get-all-favourite-products'
import { usePutFavouriteBrand } from 'features/retailer/favourites/hooks/use-put-favourite-brand'

import { useDynamoDbUserContext } from 'contexts/DynamoDbUserContext'
import PhotoViewGalleryProvider, { PhotoViewImageComponent } from 'contexts/PhotoGalleryContext'
import { useTranslate } from 'hooks'
import { BRAND_QK } from 'hooks/brand/use-get-brand-by-key'
import { useGoogleTranslate } from 'hooks/common/use-post-google-translate'
import {
  ALL_IN_ONE_PAGE_WITHOUT_LIMIT,
  CommerceToolsSearchQuery,
  useProductSearch,
} from 'hooks/product/use-product-search'
import { useProductSearchSettings } from 'hooks/product/use-product-search-settings'
import { useIsLoggedIn } from 'hooks/use-is-logged-in'
import { ProductCategory } from 'hooks/use-product-categories'

import { ErrorResponse } from 'services/error-response'
import { queryClient } from 'services/query-client'
import { roundUpToNearest, sortItemsWithOtherLast } from 'utils/common'
import { formatPrice } from 'utils/functions'
import { productSortFunc } from 'utils/product'

import { PreviewImage } from '../file-upload/image/useImageUpload'
import css from './brand-details.module.scss'

const INDEX_BIG_BRAND_IMAGE_DISPLAY = 3

export type BrandDetailsProps = {
  brandName: string
  brandLocation: string
  logoRef: string
  coverRef: string
  description: string
  videoName?: string
  videoDescription?: string
  videoRef?: string
  imgRef?: PreviewImage[]
  isRequest?: boolean
  brandKey: string
  minOrderAmount?: number
  isFavourite?: boolean
  shopStatus: boolean
  isPreview?: boolean
  minOrderAmountCurrency?: string
  openSignUp?: () => void
  productsOrder?: string[]
  primaryTypeCategories?: ProductCategory[]
}

const BrandDetails = ({
  brandName,
  brandLocation,
  logoRef,
  coverRef,
  description,
  imgRef,
  brandKey,
  minOrderAmount = 0,
  isFavourite,
  isPreview,
  minOrderAmountCurrency,
  openSignUp,
  productsOrder = [],
  primaryTypeCategories,
}: BrandDetailsProps) => {
  const router = useRouter()
  const { t, lang } = useTranslate('portal')
  const { t: tranSearch } = useTranslate('strapi-brand-[brandKey]')
  const { t: t2 } = useTranslate('product')
  const { dynamoDbUser } = useDynamoDbUserContext()
  const { isLoggedIn } = useIsLoggedIn()
  const { data: favouriteProducts } = useGetFavouriteProducts(lang, isLoggedIn)
  const { data: productSearchSettings } = useProductSearchSettings()
  const [currBrandPSearchText, setCurrBrandPSearchText] = useState<string>('')

  const params = new URLSearchParams(router.asPath.split('?')[1])

  const optionAll = { label: tranSearch('PageSearch.PageSearch_Filters_all_products'), value: '' }
  const optionNew = { label: tranSearch('PageSearch.PageSearch_Filters_new_publish'), value: 'new' }
  const optionBestSeller = { label: tranSearch('PageSearch.PageSearch_Filters_bestsellers'), value: 'bestseller' }

  const getFiltersByValue = (value: string | null) => {
    const currFilters: CommerceToolsSearchQuery = {
      offset: 0,
      limit: ALL_IN_ONE_PAGE_WITHOUT_LIMIT,
      brandKey: [brandKey],
      fullTextSearch: '',
      fuzzy: false,
      filterFacets: {
        brandKey: [brandKey],
      },
    }

    switch (value) {
      case optionNew.value: {
        const [nowTimeUnix, newProductDuration] = getNewProductDurationRange(
          productSearchSettings?.newProductDurationInDays ?? DEFAULT_NEW_PRODUCT_DURATION_IN_DAYS
        )
        currFilters.firstPublishedDateUnix = [{ from: nowTimeUnix - newProductDuration }]
        break
      }
      case optionBestSeller.value:
        currFilters.bestSeller = true
        break
      case optionAll.value:
      default: {
        const newCat = primaryTypeCategories?.find((item) => item.id === value)
        currFilters.primaryProductId = newCat?.id
      }
    }

    return currFilters
  }

  const [filters, setFilters] = useState<CommerceToolsSearchQuery>(getFiltersByValue(params.get('cat')))
  const { data: products, isLoading } = useProductSearch(filters)

  const sortedPublishedProducts = useMemo(
    () =>
      [...(products?.results ?? [])].sort((a: Product, b: Product) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
        productSortFunc(a.keyParam, b.keyParam, productsOrder)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products]
  )

  const onSuccess = () => {
    void queryClient.invalidateQueries({
      queryKey: [BRAND_QK, router.query.brandKey],
    })
    successToast(isFavourite ? t('favourites.removed_from_favourites') : t('favourites.add_to_favourites'))
  }
  const onError = useCallback(
    (errors: ErrorResponse) => {
      for (const errorResponseCodes of errors.response.data.code) {
        errorToast(t(`favourites.${errorResponseCodes}`))
      }
    },
    [t]
  )

  const { mutateAsync: setFavouriteBrand } = usePutFavouriteBrand(onSuccess, onError)

  const onSearchCurrBrand = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setFilters({ ...filters, fullTextSearch: event.currentTarget.value })
    }
  }

  const onFilterChange = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cat, ...restQuery } = router.query

    const newFilters = getFiltersByValue(value)
    setFilters(newFilters)

    const newCat = primaryTypeCategories?.find((item) => item.id === value)

    void router.replace(
      {
        query: value === optionAll.value ? restQuery : { ...restQuery, cat: newCat?.nameEn ?? value },
      },
      undefined,
      { scroll: false }
    )

    setCurrBrandPSearchText('')
  }

  const numProducts = products?.total ?? 0

  const renderBrandBigImage = useMemo(() => {
    if (!imgRef) return null

    return (
      <PhotoViewGalleryProvider>
        {imgRef.map((img, index) => (
          <PhotoView key={index} src={img.url ?? (img as unknown as string)}>
            <PhotoViewImageComponent
              src={
                imgRef[INDEX_BIG_BRAND_IMAGE_DISPLAY].url ??
                (imgRef[INDEX_BIG_BRAND_IMAGE_DISPLAY] as unknown as string)
              }
              alt={`Image`}
              width={391}
              height={250}
              className={index === INDEX_BIG_BRAND_IMAGE_DISPLAY ? css.image : css.imageHide}
              objectFit="cover"
            />
          </PhotoView>
        ))}
      </PhotoViewGalleryProvider>
    )
  }, [imgRef])

  const renderBrandImages = useMemo(() => {
    if (!imgRef) return null

    return (
      <PhotoViewGalleryProvider>
        {imgRef.map((img, index) => (
          <PhotoView key={index} src={img.url ?? (img as unknown as string)}>
            <PhotoViewImageComponent
              src={img.url ?? (img as unknown as string)}
              alt={`Image ${index}`}
              width={184}
              height={184}
              className={css.image}
              objectFit="cover"
            />
          </PhotoView>
        ))}
      </PhotoViewGalleryProvider>
    )
  }, [imgRef])

  const filterOptions = useMemo(
    () =>
      [
        optionAll,
        optionNew,
        optionBestSeller,
        ...(primaryTypeCategories ? primaryTypeCategories.map((item) => ({ label: item.name, value: item.id })) : []),
      ].sort((a, b) => sortItemsWithOtherLast(a.label, b.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [primaryTypeCategories]
  )

  const [translatedDesc, setTranslatedDesc] = useState<string>()
  const { mutateAsync: googleTranslate } = useGoogleTranslate(lang)

  useEffect(() => {
    const run = async () => {
      const response = await googleTranslate([description])
      setTranslatedDesc(unescape((response.data as string[])[0] + ` ${t2('machine_translate')}`))
    }
    if (lang.includes('zh') && description) {
      void run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description])

  return (
    <div className={css.container}>
      <div className={css.wrapper}>
        <ImageComponent
          src={coverRef}
          objectFit="cover"
          alt="Brand Cover"
          width={1268}
          height={291}
          className={css.brandCoverImage}
          quality={85}
        />
        <div className={css.details}>
          <div className={css.avatar}>
            <div className={css.image}>
              <ImageComponent src={logoRef} objectFit="cover" alt="Brand Image" width={136} height={136} />
            </div>
            <div className={css.name}>
              <div>
                <p className={css.brandName}>{brandName}</p>
                <p className={css.brandLocation}>{brandLocation}</p>
                <span className={css.minOrderAmount}>
                  {t('preview.min_order_amount', {
                    minOrderAmount: formatPrice(
                      roundUpToNearest(minOrderAmount, 1),
                      minOrderAmountCurrency ?? dynamoDbUser?.info.currency ?? 'HKD'
                    ),
                  })}
                </span>
              </div>
              {!!dynamoDbUser && (
                <FavouriteButton
                  transparentBg={true}
                  isSolid={isFavourite}
                  onToggle={() => void setFavouriteBrand(router.query.brandKey as string)}
                />
              )}
            </div>
          </div>
          <div className={css.description}>
            {!!translatedDesc && translatedDesc + '\n\n'}
            {description}
          </div>
        </div>
      </div>
      <div className={css.gallery}>
        <div className={css.bigImageWrapper}>
          {renderBrandBigImage}
          <p>{brandName}</p>
        </div>
        <div className={css.imagesWrapper}>{renderBrandImages}</div>
      </div>
      <div className={css.allProductsContainer}>
        <div className={css.header}>
          <p className={css.heading}>{t('preview.all_products')}</p>
        </div>

        <div className={css.searchWrapper}>
          <Select
            name="select-variant"
            label={`${tranSearch('PageSearch.PageSearch_Filters_filter_by')}:`}
            onChange={(value) => onFilterChange(value as string)}
            options={filterOptions}
            defaultValue={
              filterOptions.find((option) => [option.value, option.label].includes(String(params.get('cat')))) ??
              optionAll
            }
            variant="rounded"
            placeholder={t('product.category_placeholder')}
            classNames={{
              placeholder() {
                return css.selectPlaceholder
              },
              singleValue() {
                return css.inputText
              },
              container() {
                return css.selectContainer
              },
            }}
            wrapperClassName={css.selectFilterWrapper}
            isSearchable={false}
          />
          <Search
            name="search"
            value={currBrandPSearchText}
            onChange={(event) => setCurrBrandPSearchText(event.target.value)}
            onPress={onSearchCurrBrand}
            placeholder={t('preview.search_for_this_brand')}
            className={css.productSearch}
            wrapperClassName={css.productSearchWrapper}
            icon={<ImageComponent src="/images/icons/search.svg" alt={t('preview.search')} width={18} height={18} />}
          />
        </div>

        <ProductListContainer
          className={cn([!isLoading && numProducts === 0 && css.noProductsFound])}
          isLoading={isLoading}
        >
          {sortedPublishedProducts.map((product) => (
            <ProductCard
              isFavourite={favouriteProducts?.some((p) => p.keyParam === product.keyParam)}
              isBrandPreview={isPreview}
              className={css.product}
              key={product.id}
              {...product}
              openSignUp={openSignUp}
              isLoading={isLoading}
              isLoggedIn={isLoggedIn}
            />
          ))}
          {!isLoading && numProducts === 0 && (
            <NoResults icon={<RegularSearch />} message={tranSearch('PageSearch.PageSearch_Results_no_results')} />
          )}
        </ProductListContainer>
      </div>
    </div>
  )
}

export default BrandDetails
